/**
 * StageAssignModal - "Atribuir responsável" para TODOS os contatos de uma
 * etapa do funil (ou da coluna "Sem etapa"): membro da equipe ou agente de IA,
 * como o Transferir contato do inbox, só que em lote.
 *
 * Pedido do dono (2026-10-08): o cliente queria ligar o agente para todo
 * mundo de uma etapa, ou mandar todos para uma pessoa, sem abrir contato por
 * contato. Mesma linguagem visual do TransferContactModal.
 *
 * Props: { isOpen, onClose, stage: {id, name, count, isUnlabeled},
 *          pipelineId, colors, onDone(result) }
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Users, Bot, User, Check, Loader2, AlertTriangle } from 'lucide-react';
import assignmentService from '../../services/global/assignmentService';

const StageAssignModal = ({ isOpen, onClose, stage, pipelineId, colors, onDone }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setTargets([]); setPending(null); setBusy(false); setError(null); setResult(null);
    setLoading(true);
    assignmentService.getTargets()
      .then((res) => { if (!cancelled) setTargets(res?.data || []); })
      .catch(() => { if (!cancelled) setError('Não foi possível carregar a equipe.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const grouped = useMemo(() => {
    const humanos = targets.filter((t) => t.type === 'member');
    const agentes = targets.filter((t) => t.type === 'agent');
    const out = [];
    if (humanos.length) out.push({ key: 'humanos', title: 'Equipe', items: humanos });
    if (agentes.length) out.push({ key: 'agentes', title: 'Agentes de IA', items: agentes });
    return out;
  }, [targets]);

  if (!isOpen || !stage) return null;

  const confirm = async () => {
    if (!pending || busy) return;
    setBusy(true); setError(null);
    try {
      const res = await assignmentService.assignStage({
        labelId: stage.isUnlabeled ? 'unlabeled' : stage.id,
        pipelineId,
        target: pending,
      });
      const data = res?.data ?? res;
      setResult(data);
      if (typeof onDone === 'function') onDone(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao atribuir. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  const s = {
    overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    box: { width: '100%', maxWidth: 440, backgroundColor: colors.bgPrimary, borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '85vh' },
    header: { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: `1px solid ${colors.borderColor}` },
    title: { fontSize: 15, fontWeight: 600, color: colors.textPrimary, margin: 0 },
    sub: { fontSize: 12, color: colors.textSecondary, marginTop: 2 },
    body: { padding: '12px 10px', overflowY: 'auto' },
    section: { fontSize: 11, fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: 0.4, padding: '8px 8px 4px' },
    item: (active) => ({ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 10px', borderRadius: 10, border: `1px solid ${active ? colors.accentPrimary : 'transparent'}`, backgroundColor: active ? `${colors.accentPrimary}12` : 'transparent', color: colors.textPrimary, cursor: 'pointer', textAlign: 'left', fontSize: 14 }),
    footer: { padding: '12px 18px', borderTop: `1px solid ${colors.borderColor}`, display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' },
    btn: (primary) => ({ height: 36, padding: '0 14px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: primary ? 'none' : `1px solid ${colors.borderColor}`, backgroundColor: primary ? colors.accentPrimary : colors.bgPrimary, color: primary ? '#fff' : colors.textPrimary, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }),
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.box} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <Users size={20} color={colors.accentPrimary} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={s.title}>Atribuir responsável</h3>
            <div style={s.sub}>
              {stage.count} {stage.count === 1 ? 'contato' : 'contatos'} em <strong>{stage.name}</strong>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', color: colors.textSecondary, cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <div style={s.body}>
          {result ? (
            <div style={{ padding: '8px 10px', fontSize: 14, color: colors.textPrimary, lineHeight: 1.5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.accentPrimary, fontWeight: 600 }}>
                <Check size={18} /> Pronto
              </div>
              <div style={{ marginTop: 6 }}>
                {result.assigned} de {result.total} {result.total === 1 ? 'contato atribuído' : 'contatos atribuídos'} a <strong>{result.target?.name}</strong>.
                {result.failed ? ` ${result.failed} falharam.` : ''}
                {result.queued ? ' O restante está sendo aplicado em segundo plano.' : ''}
              </div>
            </div>
          ) : loading ? (
            <div style={{ padding: 20, display: 'flex', justifyContent: 'center' }}><Loader2 size={20} className="spin" color={colors.textTertiary} /></div>
          ) : grouped.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: colors.textSecondary }}>Nenhum membro ou agente disponível.</div>
          ) : grouped.map((g) => (
            <div key={g.key}>
              <div style={s.section}>{g.title}</div>
              {g.items.map((t) => {
                const Icon = t.type === 'agent' ? Bot : User;
                const active = pending?.id === t.id && pending?.type === t.type;
                return (
                  <button key={`${t.type}:${t.id}`} type="button" style={s.item(active)} onClick={() => setPending(t)} disabled={busy}>
                    <Icon size={16} color={t.type === 'agent' ? colors.accentPrimary : colors.textSecondary} />
                    <span style={{ flex: 1 }}>{t.name}</span>
                    {active && <Check size={16} color={colors.accentPrimary} />}
                  </button>
                );
              })}
            </div>
          ))}
          {pending && !result && (
            <div style={{ margin: '10px 8px 0', padding: '10px 12px', borderRadius: 10, backgroundColor: '#f59e0b14', border: '1px solid #f59e0b40', fontSize: 12, color: colors.textPrimary, display: 'flex', gap: 8, lineHeight: 1.45 }}>
              <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Todos os {stage.count} contatos de <strong>{stage.name}</strong> passam a ter <strong>{pending.name}</strong> como responsável
                {pending.type === 'agent' ? ' e o agente volta a responder cada um deles.' : '. Quem já tinha outro responsável também muda.'}
              </span>
            </div>
          )}
          {error && <div style={{ margin: '10px 8px 0', fontSize: 12, color: '#dc2626' }}>{error}</div>}
        </div>

        <div style={s.footer}>
          <button type="button" style={s.btn(false)} onClick={onClose}>{result ? 'Fechar' : 'Cancelar'}</button>
          {!result && (
            <button type="button" style={{ ...s.btn(true), opacity: pending && !busy ? 1 : 0.5 }} disabled={!pending || busy} onClick={confirm}>
              {busy ? <Loader2 size={16} /> : <Check size={16} />}
              Confirmar atribuição
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StageAssignModal;
